import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { AdhocInviteBanner } from "@/components/AdhocInviteBanner";
import { RecentAdhocMeetings } from "@/components/RecentAdhocMeetings";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/context/WebSocketContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Search,
  CheckCircle,
  CheckCircle2,
  Clock,
  Users,
  Activity,
  Loader2,
  UserCheck,
  Play,
  AlertCircle,
  MapPin,
  Plus,
  Video,
  Phone,
  Wifi,
  WifiOff,
  Pencil,
  Trash2,
  Calendar,
  CalendarCheck,
  X,
  ChevronDown,
  ChevronUp,
  Settings,
  Stethoscope,
} from "lucide-react";
import NotificationSettings from "@/components/NotificationSettings";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700 border-blue-200" },
  checked_in: { label: "Checked In", color: "bg-amber-100 text-amber-700 border-amber-200" },
  ready: { label: "Ready", color: "bg-purple-100 text-purple-700 border-purple-200" },
  in_session: { label: "In Session", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  completed: { label: "Completed", color: "bg-slate-100 text-slate-700 border-slate-200" },
  canceled: { label: "Canceled", color: "bg-red-100 text-red-700 border-red-200" },
  missed: { label: "Missed", color: "bg-red-100 text-red-700 border-red-200" },
};

const FacilitatorDashboard = () => {
  const { user, token } = useAuth();
  const { isConnected, connectionFailed, lastMessage } = useWebSocket();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [queue, setQueue] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState(null);
  
  // View state - which stat card is selected
  const [activeView, setActiveView] = useState("scheduled");
  
  // Edit/Cancel dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [cancelReason, setCancelReason] = useState("");
  
  // Waitlist state
  const [myWaitlist, setMyWaitlist] = useState([]);
  
  // My booked appointments state
  const [myBookedAppointments, setMyBookedAppointments] = useState([]);
  const [myBookedExpanded, setMyBookedExpanded] = useState(false);
  
  // Settings view state
  const [showSettings, setShowSettings] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [appointmentsRes, queueRes, sessionsRes, statsRes, waitlistRes] = await Promise.all([
        // IMPORTANT: Facilitators should ONLY see appointments they booked (booked_by_me=true)
        axios.get(`${API}/appointments?hide_past=true&booked_by_me=true`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/queue`, { headers: { Authorization: `Bearer ${token}` } }),
        // CRITICAL FIX: Facilitators should ONLY see sessions for appointments they booked
        // This prevents "crossed calls" where facilitators see video calls for other patients
        axios.get(`${API}/sessions?booked_by_me=true`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/stats/overview`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/waitlist/my`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { waitlist: [] } })),
      ]);
      
      setAppointments(appointmentsRes.data.appointments || []);
      setQueue(queueRes.data.queue || []);
      setSessions(sessionsRes.data.sessions || []);
      setStats(statsRes.data);
      setMyWaitlist(waitlistRes.data.waitlist || []);
      // myBookedAppointments is now the same as appointments since both filter by booked_by_me
      setMyBookedAppointments(appointmentsRes.data.appointments || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listen for WebSocket events and refresh data
  useEffect(() => {
    if (!lastMessage) return;

    // Req #8: ad-hoc meeting invite — short toast; the durable UX is
    // the persistent AdhocInviteBanner card at the top of the dashboard
    // (component reads /api/video/adhoc-meeting/active-invites, polls
    // every 15s, and stays visible until joined/dismissed/expired).
    if (lastMessage.type === "adhoc_meeting_invite" && lastMessage.data?.room_url) {
      const { initiator_name, topic, meeting_id } = lastMessage.data;
      toast.success(`${initiator_name || "Someone"} invited you to a video meeting`, {
        description: topic && topic !== "Ad-hoc meeting" ? topic : "See the green Join Meeting card at the top of your dashboard.",
        duration: 8000,
        action: {
          label: "Join Meeting",
          onClick: () => navigate(`/adhoc-meeting/${meeting_id}`),
        },
      });
      return;
    }

    // Refresh data on relevant events
    if (["video_call_started", "patient_checked_in", "session_started", "patient_ready", "appointment_canceled", "session_completed"].includes(lastMessage.type)) {
      fetchData();
      
      // Show special handling for video calls - ONLY if for this facilitator
      if (lastMessage.type === "video_call_started" && lastMessage.data?.session_id) {
        // CRITICAL FIX: Check if this notification is for the current facilitator
        // This prevents crossed call toasts
        const facilitatorId = lastMessage.data?.facilitator_id;
        if (facilitatorId && facilitatorId !== user?.id) {
          console.log(`[CROSSED CALL PREVENTION] Ignoring video_call_started toast - facilitator_id ${facilitatorId} !== current user ${user?.id}`);
          return; // Don't show toast for other facilitators' calls
        }
        
        const sessionId = lastMessage.data.session_id;
        toast.success(`Video call ready for ${lastMessage.data.patient_name}`, {
          duration: 15000,
          action: {
            label: "Join Call",
            onClick: () => navigate(`/session/${sessionId}`),
          },
        });
      }
    }
  }, [lastMessage, fetchData, navigate, user?.id]);

  // Aggressive polling when patients are in "ready" status waiting for therapist to start session
  // This ensures facilitators see the "Join Call" button as soon as session is created
  useEffect(() => {
    const hasReadyPatients = appointments.some(a => a.status === "ready");
    if (!hasReadyPatients) return;
    
    // Poll every 3 seconds when waiting for therapist to start session
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [appointments, fetchData]);
  
  // Aggressive polling when there are active sessions waiting for video call to start
  // This ensures the "Incoming Video Call" banner appears without needing to refresh
  useEffect(() => {
    const hasActiveSessionsWithoutVideo = sessions.some(s => 
      s.outcome === "active" && !s.daily_room_url
    );
    if (!hasActiveSessionsWithoutVideo) return;
    
    console.log("[FACILITATOR] Polling for video call to start...");
    // Poll every 2 seconds when waiting for therapist to create video room
    const interval = setInterval(fetchData, 2000);
    return () => clearInterval(interval);
  }, [sessions, fetchData]);

  // Fallback polling if WebSocket disconnects (every 30s instead of 5s)
  useEffect(() => {
    if (isConnected) return;
    
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [isConnected, fetchData]);

  const handleCheckIn = async (appointmentId) => {
    try {
      await axios.put(
        `${API}/appointments/${appointmentId}/check-in`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Patient checked in successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check in patient");
    }
  };

  const handleMarkReady = async (appointmentId) => {
    try {
      const response = await axios.put(
        `${API}/appointments/${appointmentId}/ready`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.matched) {
        toast.success(`Patient matched with ${response.data.therapist}!`);
      } else {
        toast.info(`Patient added to queue at position #${response.data.queue_position}`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark patient ready");
    }
  };

  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;
    try {
      await axios.put(
        `${API}/appointments/${selectedAppointment.id}/cancel?reason=${encodeURIComponent(cancelReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Appointment canceled successfully");
      setCancelDialogOpen(false);
      setSelectedAppointment(null);
      setCancelReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel appointment");
    }
  };

  const handleEditAppointment = async () => {
    if (!selectedAppointment) return;
    try {
      const payload = { ...editForm };

      // Build window_start/window_end if date or time changed
      const dateChanged = payload._date !== payload._originalDate;
      const startChanged = payload._startTime !== payload._originalStartTime;
      const endChanged = payload._endTime !== payload._originalEndTime;

      if (dateChanged || startChanged || endChanged) {
        const d = payload._date;
        if (d && payload._startTime) {
          payload.window_start = new Date(`${d}T${payload._startTime}:00`).toISOString();
        }
        if (d && payload._endTime) {
          payload.window_end = new Date(`${d}T${payload._endTime}:00`).toISOString();
        }
      }

      // Remove internal fields before sending
      delete payload._date;
      delete payload._startTime;
      delete payload._endTime;
      delete payload._originalDate;
      delete payload._originalStartTime;
      delete payload._originalEndTime;

      // Include browser timezone for error message formatting
      payload.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      await axios.put(
        `${API}/appointments/${selectedAppointment.id}/edit`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Appointment updated successfully");
      setEditDialogOpen(false);
      setSelectedAppointment(null);
      setEditForm({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update appointment");
    }
  };

  // Confirmation dialog state for joining a call
  const [joinConfirmSession, setJoinConfirmSession] = useState(null);

  const openEditDialog = (appointment) => {
    setSelectedAppointment(appointment);
    const aptStart = appointment.window_start ? new Date(appointment.window_start) : null;
    const aptEnd = appointment.window_end ? new Date(appointment.window_end) : null;
    // Use LOCAL date components, not UTC. toISOString() returns the UTC date,
    // which for late-evening appointments in Eastern (or any negative-offset)
    // timezones can be a day ahead of what the user actually sees on the
    // dashboard, causing the appointment to jump by ~24h when saved.
    const localDateStr = (d) => {
      if (!d) return "";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    const startDateStr = localDateStr(aptStart);
    const startTimeStr = aptStart ? aptStart.toTimeString().slice(0, 5) : "";
    const endTimeStr = aptEnd ? aptEnd.toTimeString().slice(0, 5) : "";
    setEditForm({
      patient_name: appointment.patient_name || "",
      patient_email: appointment.patient_email || "",
      contact_info: appointment.contact_info || "",
      location: appointment.location || "",
      state: appointment.state || "",
      visit_type: appointment.visit_type || "",
      required_discipline: appointment.required_discipline || "",
      _date: startDateStr,
      _startTime: startTimeStr,
      _endTime: endTimeStr,
      _originalDate: startDateStr,
      _originalStartTime: startTimeStr,
      _originalEndTime: endTimeStr,
    });
    setEditDialogOpen(true);
  };

  const openCancelDialog = (appointment) => {
    setSelectedAppointment(appointment);
    setCancelReason("");
    setCancelDialogOpen(true);
  };

  const handleCancelWaitlistEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/waitlist/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Removed from waitlist");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove from waitlist");
    }
  };

  const handleBookFromWaitlist = (entry) => {
    // Navigate to book appointment with pre-filled data
    const params = new URLSearchParams({
      waitlist_id: entry.id,
      patient_name: entry.patient_name,
      discipline: entry.required_discipline,
      state: entry.state,
      duration: entry.duration.toString(),
      location: entry.location,
      visit_type: entry.visit_type,
    });
    navigate(`/facilitator/book?${params.toString()}`);
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} at ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
  };

  // Filter appointments - only show current/future, not completed/canceled
  const scheduledAppointments = appointments
    .filter((a) => a.status === "scheduled")
    .sort((a, b) => new Date(a.window_start) - new Date(b.window_start));
  const checkedInAppointments = appointments
    .filter((a) => a.status === "checked_in")
    .sort((a, b) => new Date(a.window_start) - new Date(b.window_start));
  const activeSessions = sessions
    .filter((s) => s.outcome === "active")
    .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
  
  // GUARDRAIL: Strict filtering - only show sessions where this facilitator's user.id matches
  // session.facilitator_id. Dual-role users (facilitator+therapist) bypass this filter
  // since they act as therapist and can see their own sessions via the therapist dashboard.
  const isFacilitatorOnly = user?.roles?.includes('facilitator') && !user?.roles?.includes('therapist');
  const videoReadySessions = activeSessions.filter((s) => {
    if (!s.daily_room_url || s.video_call_ended) return false;
    // Dual-role users don't need facilitator filtering — they join as therapist
    if (!isFacilitatorOnly) return s.facilitator_id === user?.id;
    // Pure facilitators: STRICT match required
    if (s.facilitator_id !== user?.id) {
      console.log(`[GUARDRAIL] Filtered out session ${s.id} - facilitator_id="${s.facilitator_id}" !== user.id="${user?.id}"`);
      return false;
    }
    return true;
  });

  // Get content based on active view
  const getViewContent = () => {
    switch (activeView) {
      case "scheduled":
        return scheduledAppointments;
      case "checked_in":
        return checkedInAppointments;
      case "queue":
        return queue;
      case "active":
        return activeSessions;
      default:
        return [];
    }
  };

  const filteredContent = activeView === "scheduled" 
    ? scheduledAppointments.filter(
        (a) =>
          a.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.location?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : getViewContent();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="facilitator-dashboard" className="space-y-8">
        {/* Persistent incoming ad-hoc call invitations — polls the
            backend so a missed WS message doesn't lose the invite. */}
        <AdhocInviteBanner />
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
                Facilitator Dashboard
              </h1>
              {isConnected && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700">
                  <Wifi className="w-3 h-3" />
                  <span>Live</span>
                </div>
              )}
            </div>
            <p className="text-muted-foreground mt-1">
              Schedule appointments and manage patient sessions
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant={showSettings ? "default" : "outline"}
              size="icon"
              onClick={() => setShowSettings(!showSettings)}
              data-testid="settings-btn"
              title="Notification Settings"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Link to="/facilitator/book">
              <Button className="gap-2" data-testid="book-appointment-btn">
                <Plus className="w-4 h-4" />
                Book Appointment
              </Button>
            </Link>
          </div>
        </div>

        {/* Notification Settings Panel */}
        {showSettings && (
          <div className="mb-6">
            <NotificationSettings user={user} token={token} onUpdate={fetchData} />
          </div>
        )}

        {/* INCOMING VIDEO CALL ALERT - shows per-session cards with guardrails */}
        {videoReadySessions.length > 0 && (
          <div className="space-y-3">
            {videoReadySessions.map((session) => (
              <Card key={session.id} className="border-2 border-emerald-500 bg-emerald-50 animate-pulse">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                        <Video className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-emerald-700">Incoming Video Call!</h3>
                        <p className="text-emerald-800 font-medium">
                          Patient: <strong>{session.patient_name}</strong>
                        </p>
                        <p className="text-emerald-600 text-sm">
                          Therapist: {session.therapist_name}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="lg"
                      onClick={() => setJoinConfirmSession(session)}
                      className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-lg px-8 py-6"
                      data-testid={`join-video-call-btn-${session.id}`}
                    >
                      <Phone className="w-6 h-6" />
                      Accept Call
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Join Call Confirmation Dialog */}
        <Dialog open={!!joinConfirmSession} onOpenChange={(open) => { if (!open) setJoinConfirmSession(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Session Join</DialogTitle>
              <DialogDescription>
                You are about to join a live video session. Please confirm the details are correct before connecting.
              </DialogDescription>
            </DialogHeader>
            {joinConfirmSession && (
              <div className="space-y-3 py-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 space-y-2">
                  <p className="text-sm"><span className="font-semibold text-emerald-800">Patient:</span> {joinConfirmSession.patient_name}</p>
                  <p className="text-sm"><span className="font-semibold text-emerald-800">Therapist:</span> {joinConfirmSession.therapist_name}</p>
                  <p className="text-sm"><span className="font-semibold text-emerald-800">Session ID:</span> <span className="font-mono text-xs">{joinConfirmSession.id.slice(0, 8)}...</span></p>
                </div>
                <p className="text-sm text-muted-foreground">
                  This will connect you as the facilitator for this specific patient's session.
                </p>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setJoinConfirmSession(null)}>
                Not My Patient
              </Button>
              <Button
                onClick={() => { navigate(`/session/${joinConfirmSession.id}`); setJoinConfirmSession(null); }}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                data-testid="confirm-join-session-btn"
              >
                <Phone className="w-4 h-4" />
                Yes, Join Session
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Clickable Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className={`card-hover cursor-pointer transition-all ${activeView === "scheduled" ? "ring-2 ring-blue-500 bg-blue-50" : ""}`}
            onClick={() => setActiveView("scheduled")}
            data-testid="stat-scheduled"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Scheduled Today</p>
                  <p className="text-3xl font-bold mt-1">{scheduledAppointments.length}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`card-hover cursor-pointer transition-all ${activeView === "checked_in" ? "ring-2 ring-amber-500 bg-amber-50" : ""}`}
            onClick={() => setActiveView("checked_in")}
            data-testid="stat-checkedin"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Checked In</p>
                  <p className="text-3xl font-bold mt-1">{checkedInAppointments.length}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`card-hover cursor-pointer transition-all ${activeView === "queue" ? "ring-2 ring-purple-500 bg-purple-50" : ""}`}
            onClick={() => setActiveView("queue")}
            data-testid="stat-queue"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Queue</p>
                  <p className="text-3xl font-bold mt-1">{queue.length}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`card-hover cursor-pointer transition-all ${activeView === "active" ? "ring-2 ring-emerald-500 bg-emerald-50" : ""}`}
            onClick={() => setActiveView("active")}
            data-testid="stat-active"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Sessions</p>
                  <p className="text-3xl font-bold mt-1">{activeSessions.length}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Area based on selected stat */}
        <div className="space-y-4">
          {/* Search bar - only show for scheduled view */}
          {activeView === "scheduled" && (
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by patient name or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-patients-input"
                />
              </div>
            </div>
          )}

          {/* SCHEDULED VIEW */}
          {activeView === "scheduled" && (
            <>
              {filteredContent.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-semibold text-lg mb-2">No scheduled appointments</h3>
                    <p className="text-muted-foreground mb-4">
                      {searchTerm ? "No results found for your search" : "Book an appointment to get started"}
                    </p>
                    <Link to="/facilitator/book">
                      <Button>Book Appointment</Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {filteredContent.map((appointment) => (
                    <Card key={appointment.id} className="card-hover">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                              <Users className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg">{appointment.patient_name}</h3>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                <Badge variant="outline">{appointment.visit_type}</Badge>
                                <Badge variant="outline">{appointment.required_discipline}</Badge>
                                <Badge variant="outline">{appointment.state}</Badge>
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {formatDate(appointment.window_start)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {formatTime(appointment.window_start)} - {formatTime(appointment.window_end)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {appointment.location}
                                </span>
                              </div>
                              {appointment.contact_info && (
                                <p className="text-sm text-blue-600 mt-1 flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {appointment.contact_info}
                                </p>
                              )}
                              {appointment.therapist_name && (
                                <p className="text-sm mt-1 flex items-center gap-1" data-testid={`therapist-info-${appointment.id}`}>
                                  <Stethoscope className="w-3 h-3 text-primary" />
                                  <span className="font-medium">{appointment.therapist_name}</span>
                                  {appointment.therapist_phone && (
                                    <a
                                      href={`tel:${appointment.therapist_phone}`}
                                      className="text-blue-600 hover:underline"
                                      data-testid={`therapist-phone-${appointment.id}`}
                                    >
                                      • {appointment.therapist_phone}
                                    </a>
                                  )}
                                </p>
                              )}
                              {appointment.booked_by_name && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Booked by: {appointment.booked_by_name}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => openEditDialog(appointment)}
                              data-testid={`edit-${appointment.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => openCancelDialog(appointment)}
                              data-testid={`cancel-${appointment.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleCheckIn(appointment.id)}
                              data-testid={`checkin-${appointment.id}`}
                            >
                              <UserCheck className="w-4 h-4 mr-2" />
                              Check In
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}

          {/* CHECKED IN VIEW */}
          {activeView === "checked_in" && (
            <>
              {checkedInAppointments.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <UserCheck className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-semibold text-lg mb-2">No patients checked in</h3>
                    <p className="text-muted-foreground">
                      Check in patients from the Scheduled view first
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {checkedInAppointments.map((appointment) => (
                    <Card key={appointment.id} className="border-amber-200 bg-amber-50/30">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                              <UserCheck className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-lg">{appointment.patient_name}</h3>
                                <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                  Checked In
                                </Badge>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                <Badge variant="outline">{appointment.visit_type}</Badge>
                                <Badge variant="outline">{appointment.required_discipline}</Badge>
                                <Badge variant="outline">{appointment.state}</Badge>
                              </div>
                              {appointment.contact_info && (
                                <p className="text-sm text-blue-600 mt-1 flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {appointment.contact_info}
                                </p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {formatDate(appointment.window_start)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {formatTime(appointment.window_start)} - {formatTime(appointment.window_end)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => openCancelDialog(appointment)}
                              data-testid={`cancel-checkedin-${appointment.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button 
                                  className="bg-accent hover:bg-accent/90 text-accent-foreground"
                                  data-testid={`ready-now-${appointment.id}`}
                                >
                                  <Play className="w-4 h-4 mr-2" />
                                  Ready Now
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Confirm Patient Ready</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will add {appointment.patient_name} to the matching queue. 
                                    They will be matched with an available therapist based on their requirements.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleMarkReady(appointment.id)}>
                                    Confirm Ready
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}

          {/* QUEUE VIEW */}
          {activeView === "queue" && (
            <>
              {queue.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Queue is empty</h3>
                    <p className="text-muted-foreground">
                      Patients waiting for therapist matching will appear here
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {queue.map((entry) => (
                    <Card key={entry.id}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-lg">
                            #{entry.position}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold">{entry.patient_name}</h3>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <Badge variant="outline">{entry.visit_type}</Badge>
                              <Badge variant="outline">{entry.required_discipline}</Badge>
                              <Badge variant="outline">{entry.state}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                              Ready since: {formatDateTime(entry.ready_at)}
                              {entry.estimated_wait && ` • Est. wait: ${entry.estimated_wait} min`}
                            </p>
                          </div>
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Matching...
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ACTIVE SESSIONS VIEW */}
          {activeView === "active" && (
            <>
              {activeSessions.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Activity className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-semibold text-lg mb-2">No active sessions</h3>
                    <p className="text-muted-foreground">
                      Active telehealth sessions will appear here
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {activeSessions.map((session) => (
                    <Card key={session.id} className={session.daily_room_url && !session.video_call_ended ? "border-emerald-300 bg-emerald-50/30" : ""}>
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center relative">
                              <Activity className="w-6 h-6 text-emerald-600" />
                              {session.daily_room_url && !session.video_call_ended && (
                                <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 pulse-indicator" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold">{session.patient_name}</h3>
                                <span className="text-muted-foreground">with</span>
                                <h3 className="font-semibold">{session.therapist_name}</h3>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                Started: {formatDateTime(session.start_time)}
                              </p>
                              {session.daily_room_url && !session.video_call_ended ? (
                                <Badge className="mt-2 bg-emerald-500 text-white">
                                  <Video className="w-3 h-3 mr-1" />
                                  Video Call Active
                                </Badge>
                              ) : (
                                <Badge className="mt-2 bg-emerald-100 text-emerald-700 border-emerald-200">
                                  Session Active
                                </Badge>
                              )}
                            </div>
                          </div>
                          {session.daily_room_url && !session.video_call_ended && (
                            <Button
                              onClick={() => navigate(`/session/${session.id}`)}
                              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`join-session-${session.id}`}
                            >
                              <Video className="w-4 h-4" />
                              Join Video Call
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* My Waitlist Section */}
        {myWaitlist.filter(w => w.status === "pending").length > 0 && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="font-['Manrope'] text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                My Waitlist
                <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-700 border-amber-300">
                  {myWaitlist.filter(w => w.status === "pending").length} pending
                </Badge>
              </CardTitle>
              <CardDescription>
                Patients waiting for available appointments
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {myWaitlist
                  .filter(w => w.status === "pending")
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map((entry) => (
                    <div 
                      key={entry.id} 
                      className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-200"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <Users className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">{entry.patient_name}</h4>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{entry.required_discipline}</Badge>
                            <Badge variant="outline" className="text-xs">{entry.state}</Badge>
                            <span className="text-xs text-muted-foreground">{entry.visit_type}</span>
                            <span className="text-xs text-muted-foreground">• {entry.duration} min</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Added {new Date(entry.created_at).toLocaleDateString("en-US", { 
                              month: "short", 
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit"
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleBookFromWaitlist(entry)}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`book-waitlist-${entry.id}`}
                        >
                          <Calendar className="w-4 h-4 mr-1" />
                          Book Now
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelWaitlistEntry(entry.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Booked Appointments Section - Collapsible */}
        <Card>
          <CardHeader 
            className="pb-3 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setMyBookedExpanded(!myBookedExpanded)}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="font-['Manrope'] text-lg flex items-center gap-2">
                <CalendarCheck className="w-5 h-5 text-primary" />
                My Booked Appointments
                <Badge variant="outline" className="ml-2">
                  {myBookedAppointments.length} total
                </Badge>
                {myBookedAppointments.filter(a => a.status === "scheduled" || a.status === "checked_in").length > 0 && (
                  <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs">
                    {myBookedAppointments.filter(a => a.status === "scheduled" || a.status === "checked_in").length} upcoming
                  </Badge>
                )}
              </CardTitle>
              {myBookedExpanded ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </div>
            {!myBookedExpanded && (
              <CardDescription className="mt-1">
                Click to expand • {myBookedAppointments.filter(a => a.status === "completed").length} completed, {myBookedAppointments.filter(a => a.status === "canceled").length} canceled
              </CardDescription>
            )}
          </CardHeader>
          {myBookedExpanded && (
          <CardContent>
            {myBookedAppointments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>You haven't booked any appointments yet</p>
                <Link to="/facilitator/book">
                  <Button variant="outline" className="mt-3">
                    Book Your First Appointment
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {myBookedAppointments
                  .sort((a, b) => new Date(b.window_start) - new Date(a.window_start))
                  .map((apt) => {
                    const aptDate = new Date(apt.window_start);
                    const isPast = aptDate < new Date();
                    const isToday = new Date().toDateString() === aptDate.toDateString();
                    const canModify = !["completed", "canceled", "in_session"].includes(apt.status);
                    
                    return (
                      <div 
                        key={apt.id} 
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          isPast ? "bg-muted/30 border-muted" : 
                          isToday ? "bg-blue-50 border-blue-200" : 
                          "bg-white border-border"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            apt.status === "completed" ? "bg-emerald-100" :
                            apt.status === "canceled" ? "bg-red-100" :
                            apt.status === "in_session" ? "bg-purple-100" :
                            apt.status === "ready" ? "bg-amber-100" :
                            "bg-blue-100"
                          }`}>
                            {apt.status === "completed" ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> :
                             apt.status === "canceled" ? <X className="w-5 h-5 text-red-600" /> :
                             apt.status === "in_session" ? <Video className="w-5 h-5 text-purple-600" /> :
                             <Calendar className="w-5 h-5 text-blue-600" />}
                          </div>
                          <div>
                            <h4 className="font-medium">{apt.patient_name}</h4>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <Badge 
                                variant={
                                  apt.status === "completed" ? "default" :
                                  apt.status === "canceled" ? "destructive" :
                                  apt.status === "in_session" ? "default" :
                                  "outline"
                                }
                                className="text-xs"
                              >
                                {apt.status}
                              </Badge>
                              <span className="text-xs text-muted-foreground">{apt.required_discipline}</span>
                              <span className="text-xs text-muted-foreground">• {apt.state}</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {aptDate.toLocaleDateString("en-US", { 
                                weekday: "short",
                                month: "short", 
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit"
                              })}
                              {isToday && <Badge variant="outline" className="ml-2 text-xs bg-blue-100">Today</Badge>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {apt.therapist_name && (
                            <div className="text-right mr-4">
                              <p className="text-xs text-muted-foreground">Therapist</p>
                              <p className="text-sm font-medium">{apt.therapist_name}</p>
                              {apt.therapist_phone && (
                                <a
                                  href={`tel:${apt.therapist_phone}`}
                                  className="text-xs text-blue-600 hover:underline"
                                  data-testid={`booked-therapist-phone-${apt.id}`}
                                >
                                  {apt.therapist_phone}
                                </a>
                              )}
                            </div>
                          )}
                          {canModify && (
                            <>
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => openEditDialog(apt)}
                                data-testid={`edit-booked-${apt.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => openCancelDialog(apt)}
                                data-testid={`cancel-booked-${apt.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
          )}
        </Card>

        {/* Edit Appointment Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Appointment</DialogTitle>
              <DialogDescription>
                Update appointment details for <strong>{selectedAppointment?.patient_name}</strong>. Changes won't affect completed or in-session appointments.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="edit_patient_name">Patient Name</Label>
                  <Input
                    id="edit_patient_name"
                    value={editForm.patient_name || ""}
                    onChange={(e) => setEditForm({ ...editForm, patient_name: e.target.value })}
                    data-testid="edit-patient-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="edit_patient_email">Patient Email</Label>
                  <Input
                    id="edit_patient_email"
                    type="email"
                    value={editForm.patient_email || ""}
                    onChange={(e) => setEditForm({ ...editForm, patient_email: e.target.value })}
                    data-testid="edit-patient-email-input"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit_contact_info">
                  Contact Info (Phone/Email) <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="edit_contact_info"
                  placeholder="e.g. 555-1234 or patient@email.com"
                  value={editForm.contact_info || ""}
                  onChange={(e) => setEditForm({ ...editForm, contact_info: e.target.value })}
                  required
                  data-testid="edit-contact-info-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit_notes">Notes</Label>
                <textarea
                  id="edit_notes"
                  placeholder="Preferred therapist, accommodations, or any other details…"
                  value={editForm.notes || ""}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={2}
                  data-testid="edit-notes-input"
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                <p className="text-xs text-muted-foreground">Optional — e.g. preferred therapist, hearing aid, interpreter.</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit_location">Facility</Label>
                <Input
                  id="edit_location"
                  value={editForm.location || ""}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  data-testid="edit-location-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Appointment Date & Time</Label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Input
                      type="date"
                      value={editForm._date || ""}
                      onChange={(e) => setEditForm({ ...editForm, _date: e.target.value })}
                      data-testid="edit-date-input"
                    />
                  </div>
                  <div>
                    <Select
                      value={editForm._startTime || ""}
                      onValueChange={(val) => setEditForm({ ...editForm, _startTime: val })}
                    >
                      <SelectTrigger data-testid="edit-start-time-select">
                        <SelectValue placeholder="Start" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {Array.from({ length: 96 }, (_, i) => {
                          const h = Math.floor(i / 4);
                          const m = (i % 4) * 15;
                          const val = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
                          const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                          const ampm = h < 12 ? "AM" : "PM";
                          return <SelectItem key={val} value={val}>{`${h12}:${m.toString().padStart(2, "0")} ${ampm}`}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Select
                      value={editForm._endTime || ""}
                      onValueChange={(val) => setEditForm({ ...editForm, _endTime: val })}
                    >
                      <SelectTrigger data-testid="edit-end-time-select">
                        <SelectValue placeholder="End" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {Array.from({ length: 96 }, (_, i) => {
                          const h = Math.floor(i / 4);
                          const m = (i % 4) * 15;
                          const val = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
                          const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                          const ampm = h < 12 ? "AM" : "PM";
                          return <SelectItem key={val} value={val}>{`${h12}:${m.toString().padStart(2, "0")} ${ampm}`}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Changing time will be checked for double-booking conflicts</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Visit Type</Label>
                  <Select
                    value={editForm.visit_type || ""}
                    onValueChange={(val) => setEditForm({ ...editForm, visit_type: val })}
                  >
                    <SelectTrigger data-testid="edit-visit-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="evaluation">Evaluation (15 min)</SelectItem>
                      <SelectItem value="supervisory">Supervisory (15 min)</SelectItem>
                      <SelectItem value="st_evaluation">ST Evaluation (45 min)</SelectItem>
                      <SelectItem value="st_treatment">ST Treatment (30 min)</SelectItem>
                      <SelectItem value="follow-up">Follow-up</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Discipline</Label>
                  <Select
                    value={editForm.required_discipline || ""}
                    onValueChange={(val) => setEditForm({ ...editForm, required_discipline: val })}
                  >
                    <SelectTrigger data-testid="edit-discipline-select">
                      <SelectValue placeholder="Select discipline" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PT">Physical Therapy (PT)</SelectItem>
                      <SelectItem value="OT">Occupational Therapy (OT)</SelectItem>
                      <SelectItem value="SLP">Speech Language Pathology (SLP)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit_state">State</Label>
                <Input
                  id="edit_state"
                  placeholder="e.g. California"
                  value={editForm.state || ""}
                  onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                  data-testid="edit-state-input"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleEditAppointment} data-testid="save-edit-appointment-btn">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Appointment Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel Appointment</DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel the appointment for {selectedAppointment?.patient_name}? 
                An email notification will be sent.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="cancel_reason">Reason for Cancellation (optional)</Label>
                <Textarea
                  id="cancel_reason"
                  placeholder="Enter a reason for cancellation..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                Keep Appointment
              </Button>
              <Button variant="destructive" onClick={handleCancelAppointment}>
                Cancel Appointment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Recent ad-hoc meetings — rejoin a dropped call in one click. */}
        <RecentAdhocMeetings />
      </div>
    </Layout>
  );
};

export default FacilitatorDashboard;
